import * as React from 'react'
import {
  CheckIcon,
  GlobeIcon,
  Link1Icon,
  LinkBreak1Icon,
} from '@radix-ui/react-icons'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/sotano-kit/primitives/popover'
import { $toggleItem } from '..'
import { ToolbarToggleItem } from '@radix-ui/react-toolbar'
import { Editor, useEditorState } from '@tiptap/react'
import { $input } from '~/sotano-kit/primitives/input'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { BlockIcon } from '~/components/block-icon'
import { clsx } from 'clsx'
import { getBlockTitleForDisplay } from '~/lib/get-block-title-for-display'
import { RichTextBlock } from '@basehub/data/replicache/index'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { LinkAttributes } from '@basehub/tiptap-api-utils'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import {
  parseTab,
  useTabsStore,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { useRepoStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import { Checkbox } from '~/sotano-kit/primitives/checkbox'
import { useRichTextContext } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/rich-text/context'
import {
  InsertLinkOption,
  NEW_LINK_OPTION,
  useLinkOptions,
} from './utils/use-link-options'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'

// Separate options interface

export const InsertLinkAction = React.memo(
  ({
    editor,
    containerRef,
    disabled,
    block,
  }: {
    editor: Editor
    containerRef: React.RefObject<null | HTMLDivElement>
    disabled?: boolean
    block: RichTextBlock
  }) => {
    const [search, setSearch] = React.useState('')
    const [targetLink, setTargetLink] = React.useState<string | undefined>()
    const { isLinkModalOpen: isOpen, setIsLinkModalOpen: setIsOpen } =
      useRichTextContext()
    const [selectedIndex, setSelectedIndex] = React.useState(0)
    const listRef = React.useRef<HTMLUListElement>(null)

    // Get the current `tiptap` link state
    const { isActiveLink, markAttributes } = useEditorState({
      editor,
      selector: ({ editor }) => {
        return {
          isActiveLink: editor.isActive('link'),
          markAttributes: editor.getAttributes('link') as LinkAttributes,
        }
      },
    })

    const { filteredOptions } = useLinkOptions({
      search,
      block,
      markAttributes,
    })

    //================================================
    // Handlers 📠
    //================================================
    const handleSelect = React.useCallback(
      (option: InsertLinkOption) => {
        if (option.value === NEW_LINK_OPTION) {
          editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: search, target: targetLink, type: 'link' })
            .run()
        } else {
          editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({
              type: 'internal',
              targetId: option.value,
              target: targetLink,
            })
            .run()
        }
        setIsOpen(false)
      },
      [editor, search, targetLink, setIsOpen]
    )

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent) => {
        e.stopPropagation()
        const scrollToSelectedOption = (i: number) => {
          const selectedOption = listRef.current?.querySelector(
            `[data-option-index="${i}"]`
          ) as HTMLElement
          if (selectedOption) {
            selectedOption.scrollIntoView({
              block: 'nearest',
              inline: 'nearest',
            })
          }
        }

        switch (e.key) {
          case 'ArrowDown': {
            e.preventDefault()
            const newIndex = Math.min(
              selectedIndex + 1,
              filteredOptions.length - 1
            )
            setSelectedIndex(newIndex)
            scrollToSelectedOption(newIndex)
            break
          }
          case 'ArrowUp': {
            e.preventDefault()
            const newIndex = Math.max(selectedIndex - 1, 0)
            setSelectedIndex(newIndex)
            scrollToSelectedOption(newIndex)
            break
          }
          case 'Enter': {
            e.preventDefault()
            const option = filteredOptions[selectedIndex]
            if (option) {
              handleSelect(option)
            }
            break
          }
        }
      },
      [filteredOptions, selectedIndex, handleSelect, setSelectedIndex]
    )

    const onCheckedChange = React.useCallback(
      (checked: boolean) => {
        if (isActiveLink) {
          editor
            .chain()
            .extendMarkRange('link')
            .setMark('link', {
              ...markAttributes,
              target: checked ? '_BLANK' : '',
            })
            .run()
        }
        setTargetLink(checked ? '_BLANK' : undefined)
      },
      [editor, markAttributes, isActiveLink]
    )

    // =================================================
    // Effects 🎣
    // =================================================

    // Handles the selected index when the options change and the selected index is out of bounds
    React.useEffect(() => {
      if (selectedIndex > filteredOptions.length - 1) {
        setSelectedIndex(0)
      }
    }, [filteredOptions, selectedIndex, setSelectedIndex])

    // Handles the target link when the mark attributes change
    React.useEffect(() => {
      if (markAttributes.target?.toUpperCase() === '_BLANK') {
        setTargetLink('_BLANK')
      } else setTargetLink(undefined)
    }, [markAttributes, setTargetLink])

    // Handles the search when the mark attributes change
    React.useEffect(() => {
      if (!isOpen) setSearch('')
      else if (markAttributes.type === 'link' && markAttributes.href) {
        setSearch(markAttributes.href)
      }
    }, [isOpen, markAttributes, setSearch])

    // =================================================
    // Render  🎨
    // =================================================
    const shouldRenderResults =
      !isActiveLink ||
      (isActiveLink &&
        markAttributes.type === 'link' &&
        markAttributes.href !== search) ||
      (isActiveLink && markAttributes.type === 'internal')

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <ToolbarToggleItem
            value="link"
            aria-label="Link"
            disabled={disabled}
            className={$toggleItem({ active: isActiveLink })}
          >
            <Link1Icon />
          </ToolbarToggleItem>
        </PopoverTrigger>
        <PopoverContent
          className="!p-0 min-w-[320px]"
          data-rt-editor-popover-id={block.id}
          ref={containerRef}
          sideOffset={8}
          onCloseAutoFocus={(e) => {
            e.preventDefault()
          }}
        >
          <div
            className={clsx(
              'gap-x-1.5 gap-y-2.5 flex items-center flex-1 h-full py-[11px] px-3 relative'
            )}
          >
            <div className={clsx($input({ class: 'flex !p-0', size: 'sm' }))}>
              {isActiveLink &&
                markAttributes.type === 'internal' &&
                search === '' && <InternalLink {...markAttributes} />}
              <input
                placeholder={!isActiveLink ? 'Reference or URL' : ''}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                }}
                onKeyDown={handleKeyDown}
                className="w-full flex-1 outline-none !px-2 !bg-transparent placeholder:text-dark-faint"
              />
            </div>
            {isActiveLink && (
              <SimpleTooltip content="Remove link">
                <IconButton
                  onClick={() => {
                    editor.commands.unsetLink()
                    setSearch('')
                    setIsOpen(false)
                  }}
                >
                  <LinkBreak1Icon />
                </IconButton>
              </SimpleTooltip>
            )}
          </div>
          {shouldRenderResults && (
            <ul
              className="flex flex-col max-h-[300px] max-w-[320px] overflow-y-auto"
              ref={listRef}
            >
              {!search && filteredOptions.length > 0 && (
                <p className="text-xs text-dark-faint px-3 font-medium mb-2">
                  Recent blocks
                </p>
              )}
              {filteredOptions.map((option, index) => (
                <LinkOptionItem
                  key={option.value}
                  option={option}
                  isSelected={selectedIndex === index}
                  onSelect={() => handleSelect(option)}
                  onHover={() => setSelectedIndex(index)}
                  isActive={
                    isActiveLink &&
                    markAttributes.type === 'internal' &&
                    markAttributes.targetId === option?.value
                  }
                  search={search}
                  data-option-index={index}
                />
              ))}
            </ul>
          )}
          <div className="bg-dark-base-dark py-2 px-3 pl-4 flex items-center gap-2 border-t border-dark-border-solid">
            <label className="flex gap-2 items-center">
              <Checkbox
                onCheckedChange={onCheckedChange}
                checked={targetLink === '_BLANK'}
              />
              <span className="text-xs text-dark-faint">Open in new tab</span>
            </label>
          </div>
        </PopoverContent>
      </Popover>
    )
  }
)

type CurrentSettedLinkProps = LinkAttributes & {
  onRemove?: () => void
  richTextBlock?: RichTextBlock
}

const InternalLink = ({ richTextBlock, ...props }: CurrentSettedLinkProps) => {
  const { teamSlug, repoSlug, branchOrCommit } = useDashboardSlugs()
  const getNode = useWorkingTreeStore((state) => state.treeRef.current?.getNode)
  const block =
    props.type === 'internal' ? getNode?.(props.targetId)?.value : null
  const addTab = useTabsStore((state) => state.addTab)
  const setFocus = useRepoStore((s) => s.setFocus)
  const currentTab = useTabsStore((s) =>
    s.activeTab ? parseTab(s.activeTab) : null
  )
  const blockTab =
    currentTab && currentTab.type === 'block'
      ? (getNode?.(currentTab.blockId)?.value ?? richTextBlock)
      : richTextBlock

  if (!block) return null
  return (
    <>
      <ButtonLink
        unstyled
        href={`/${teamSlug}/${repoSlug}/explore/${branchOrCommit}/${idToSourceBlockId(
          block
        )}`}
        className="flex items-center justify-between2 hover:underline leading-none pr-1.5 pl-4 text-xs gap-2 left-2 top-1/2 absolute transform -translate-y-1/2"
        onClick={() => {
          addTab({ key: block.id, partiallyOpen: true })
          setFocus({
            type: 'block:document-title',
            blockId: idToSourceBlockId(block),
            temporaryBackLink: blockTab
              ? {
                  id: blockTab.id,
                  title: blockTab.title ?? 'Untitled',
                }
              : undefined,
          })
        }}
      >
        <span className="relative">
          <BlockIcon
            block={block}
            iconProps={{ className: 'text-dark-muted' }}
          />
        </span>
        <span>{getBlockTitleForDisplay(block.title)}</span>
      </ButtonLink>
    </>
  )
}

const LinkOptionItem = ({
  option,
  isSelected,
  onSelect,
  onHover,
  search,
  isActive,
  ...rest
}: {
  option: InsertLinkOption
  isSelected: boolean
  onSelect: () => void
  onHover: () => void
  search: string
  isActive?: boolean
}) => {
  if (option.value === NEW_LINK_OPTION) {
    return (
      <li
        {...rest}
        className={clsx(
          'text-xs text-dark-faint px-3 py-2 flex flex-col cursor-default',
          isSelected && 'bg-dark-shade-hover'
        )}
        onMouseEnter={onHover}
        onClick={onSelect}
      >
        <div className="flex items-center gap-2 font-medium w-full">
          <GlobeIcon className={clsx('text-dark-bright size-4')} />
          <span
            className={clsx(
              'max-w-[280px] text-dark-bright text-sm w-full truncate'
            )}
          >
            {search}
          </span>
        </div>
        <span className="text-xs text-dark-faint pl-6">
          Enter a full URL to create a link.
        </span>
      </li>
    )
  }

  return (
    <li
      {...rest}
      className={clsx(
        'flex items-center gap-2 p-2 px-3 cursor-default text-xs relative',
        isSelected && 'bg-dark-shade-hover',
        isActive && '!text-dark-control-base'
      )}
      onMouseEnter={onHover}
      onClick={onSelect}
    >
      <div className="flex flex-col">
        <div className="flex items-start gap-2">
          <BlockIcon
            block={option.node ?? { type: 'instance' }}
            iconProps={{ className: 'mt-0.5' }}
          />
          <span
            className={clsx(
              'text-xs font-medium text-dark-bright',
              isActive && 'text-dark-control-base'
            )}
          >
            {option.label}
          </span>
        </div>
        {option.breadcrumbs && (
          <span
            className={clsx(
              'text-dark-faint pl-6',
              isActive && '!text-dark-control-base/70'
            )}
          >
            {option.breadcrumbs.join(' / ')}
          </span>
        )}
      </div>
      {isActive && (
        <CheckIcon className="text-dark-control-base absolute right-4 top-5 size-4" />
      )}
    </li>
  )
}
